import axios from "axios";
import React, { useState } from "react";
import {
  CheckCircle,
  Eye,
  ShieldCheck,
  X,
} from "react-bootstrap-icons";

import { ApiConnectionString } from "../constants/ApiConntection";
import { useAuthContext } from "../hooks/useAuthContext";
import "./SubscribeModal.css";

export default function SubscribeModal({ show, onClose }) {
  const { user } = useAuthContext();

  const [isLoading, setIsLoading] = useState(false);
  const [err, setErr] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("monthly");

  const plans = {
    monthly: {
      label: "Monthly",
      price: "£11.99/month",
      buttonText: "Subscribe for £11.99/month",
    },
    yearly: {
      label: "Yearly",
      price: "£119/year",
      saving: "Save £24.88",
      buttonText: "Subscribe for £119/year",
    },
  };

  const selectedPrice = plans[selectedPlan];

  const startCheckout = async () => {
    if (!user?.token) {
      setErr("You need to be logged in to subscribe.");
      return;
    }

    try {
      setIsLoading(true);
      setErr("");

      const response = await axios.post(
        `${ApiConnectionString}/api/checkout/start/${selectedPlan}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      const checkoutUrl = response.data?.url || response.data;

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        setErr("Checkout could not be started.");
      }
    } catch (error) {
      console.error(error);
      setErr("Failed to start checkout. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!show) {
    return null;
  }

  return (
    <div className="subscribe-backdrop">
      <div className="subscribe-modal">
        <button
          type="button"
          className="subscribe-close-btn"
          onClick={onClose}
          aria-label="Close subscribe modal"
          disabled={isLoading}
        >
          <X />
        </button>

        <div className="subscribe-header">
          <span className="subscribe-badge">Trial ended</span>

          <h2>Subscribe to continue</h2>

          <p>
            Your free trial has ended. Subscribe to keep creating and updating
            appointments, clients and availability.
          </p>
        </div>

        {err && <div className="alert alert-danger">{err}</div>}

        <div className="subscribe-plan-options">
          <button
            type="button"
            className={`subscribe-plan-option ${
              selectedPlan === "monthly" ? "active" : ""
            }`}
            onClick={() => setSelectedPlan("monthly")}
            disabled={isLoading}
          >
            <span>Monthly</span>
            <strong>£11.99/month</strong>
          </button>

          <button
            type="button"
            className={`subscribe-plan-option ${
              selectedPlan === "yearly" ? "active" : ""
            }`}
            onClick={() => setSelectedPlan("yearly")}
            disabled={isLoading}
          >
            <span>Yearly</span>
            <strong>£119/year</strong>
            <small>Save £24.88</small>
          </button>
        </div>

        <div className="subscribe-readonly-card">
          <div className="subscribe-readonly-icon">
            <Eye />
          </div>

          <div>
            <strong>You can still view your diary</strong>

            <p>
              Your diary and client information remain visible, but editing is
              paused until you subscribe.
            </p>
          </div>
        </div>

        <div className="subscribe-benefits">
          <div className="subscribe-benefit-row">
            <CheckCircle />
            <span>Create and update appointments</span>
          </div>

          <div className="subscribe-benefit-row">
            <CheckCircle />
            <span>Manage clients</span>
          </div>

          <div className="subscribe-benefit-row">
            <CheckCircle />
            <span>Update availability and booking rules</span>
          </div>

          <div className="subscribe-benefit-row">
            <CheckCircle />
            <span>Send appointment confirmation emails</span>
          </div>
        </div>

        <div className="subscribe-security-card">
          <ShieldCheck />

          <p>
            Payments are handled securely through Stripe. Therapy Diary does not
            store your card details.
          </p>
        </div>

        <div className="subscribe-actions">
          <button
            type="button"
            className="subscribe-primary-btn"
            onClick={startCheckout}
            disabled={isLoading}
          >
            {isLoading ? "Opening checkout..." : selectedPrice.buttonText}
          </button>

          <button
            type="button"
            className="subscribe-secondary-btn"
            onClick={onClose}
            disabled={isLoading}
          >
            Keep viewing diary
          </button>
        </div>
      </div>
    </div>
  );
}